import { useEffect } from 'react';

// A link to a page in a DIFFERENT frontend module.
//
// Every module is its own React Router app with its own route table, so a
// <Link to="/sign-in"> rendered inside the web module does not navigate -
// it asks that module's router for a route it does not have and lands on
// its 404. Crossing a module boundary is a document navigation, which means
// a plain anchor.
//
// This exists as a named component rather than a bare <a> so the boundaries
// are greppable: `git grep ModuleLink` is the list of every place the
// frontend leaves one bundle for another, which is exactly the list to
// re-read when a route moves between modules.
//
// See docs/frontend-modules-plan.md §5 for which module owns which path.
export default function ModuleLink({ to, children, ...rest }) {
  return (
    <a href={to} {...rest}>
      {children}
    </a>
  );
}

// The imperative form, for the handful of places that navigate from an
// event handler rather than a click on a link (landing.jsx's upload
// handoffs, sign-in on success). Deliberately not a hook - there is no
// router state to consult, a module boundary is always a full document load.
export function navigateToModule(to) {
  window.location.assign(to);
}

// The declarative form: what <Navigate to="..."/> would be if the
// destination were in this module. Render it instead of returning
// <Navigate>, which would ask this module's router for a route it does not
// have.
//
// The assign() happens in an effect rather than during render, because it is
// a side effect and React may render a component more than once before
// committing it - calling it inline would fire the navigation from a render
// that was then thrown away. Renders null in the meantime: every call site
// is a gate that has already decided not to show its content, and the dark
// body background covers the instant before the document is replaced.
export function ModuleRedirect({ to }) {
  useEffect(() => {
    window.location.assign(to);
  }, [to]);
  return null;
}
